import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export default function Modal({ isOpen, onClose, title, children, maxWidth = '560px' }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  // Rendu via portail sur <body> : indispensable pour que `position: fixed`
  // se cale sur la fenêtre et non sur un ancêtre porteur d'un backdrop-filter /
  // transform / filter (ex. le <header>), qui créerait un bloc de confinement
  // et tronquerait la modale. Les variables CSS de thème sont posées sur :root
  // et cascadent jusqu'au body, le thème reste donc correct.
  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto" data-testid="modal-overlay">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div
          className="relative z-10 w-full rounded-[16px] flex flex-col overflow-hidden"
          style={{
            maxWidth,
            maxHeight: '90vh',
            background: 'var(--widget-bg)',
            border: '1px solid var(--border-color)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b" style={{borderColor:'var(--border-color)'}}>
            <h2 className="text-[15px] font-medium ">{title}</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--border-color)] transition-colors" data-testid="modal-close-button">
              <X className="w-4 h-4 text-[var(--text-muted)]" />
            </button>
          </div>
          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Reusable form field components
export function FormField({ label, children, required }) {
  return (
    <div className="mb-4">
      <label className="block text-[11px] uppercase tracking-[1px] text-[var(--text-tertiary)] mb-2">
        {label} {required && <span className="text-[#C9A227]">*</span>}
      </label>
      {children}
    </div>
  );
}

export function FormInput({ value, onChange, placeholder, type = 'text', required, ...props }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full px-3 py-2.5 rounded-[8px] text-[13px]  outline-none transition-colors focus:border-[rgba(201,162,39,0.4)] placeholder:text-[var(--text-tertiary)]"
      style={{background:'var(--bg-tertiary)', border:'1px solid var(--border-color)'}}
      {...props}
    />
  );
}

export function FormSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2.5 rounded-[8px] text-[13px] outline-none transition-colors"
      style={{
        background:'var(--bg-tertiary)', 
        border:'1px solid var(--border-color)',
        color: 'var(--text-primary)'
      }}
    >
      {placeholder && <option value="" style={{background:'var(--bg-primary)', color:'var(--text-primary)'}}>{placeholder}</option>}
      {options.map(opt => (
        <option key={opt.value} value={opt.value} style={{background:'var(--bg-primary)', color:'var(--text-primary)'}}>{opt.label}</option>
      ))}
    </select>
  );
}

export function FormTextarea({ value, onChange, placeholder, rows = 3, ...props }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2.5 rounded-[8px] text-[13px]  outline-none transition-colors resize-none placeholder:text-[var(--text-tertiary)]"
      style={{background:'var(--bg-tertiary)', border:'1px solid var(--border-color)'}}
      {...props}
    />
  );
}

export function FormButton({ onClick, children, variant = 'primary', disabled, type = 'button' }) {
  const styles = variant === 'primary'
    ? { background: 'var(--text-primary)', color: 'var(--bg-primary)' }
    : { background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`px-5 py-2.5 rounded-[8px] text-[13px] font-semibold tracking-[0.5px] transition-colors disabled:opacity-50 ${variant === 'primary' ? 'hover:brightness-110' : 'hover:bg-[var(--border-color)]'}`}
      style={styles}
    >
      {children}
    </button>
  );
}

export function FormRow({ children }) {
  return <div className="grid grid-cols-2 gap-4">{children}</div>;
}
